<template>
  <b-modal modal-class="employee-respond-to-referral-request" :id="id" v-model="visible" lazy hide-footer hide-header>
    <template v-slot:modal-header>
      <empty-slot v-if="$apollo.loading"/>
      <h5 v-else-if="state === 'ERRORED'">An unexpected error occurred</h5>
      <b-container fluid v-else-if="state !== 'ERRORED'">
        err
      </b-container>
      <empty-slot v-else/>
    </template>

    <b-container fluid v-if="!referral">
      <loading-spinner class="loading-panel"/>
    </b-container>

    <b-container fluid v-else-if="err">
      <b-alert show variant="danger">
        <pre>{{ err }}</pre>
      </b-alert>
    </b-container>

    <template v-else-if="state === 'NO' || state === 'NOT_SURE'">
      <b-col cols="12" class="content">
        <h3 v-if="state === 'NOT_SURE'">Not sure if you want to recommend your contact <b> ‘{{ contact.fullName }}’</b> ?</h3>
        <h3 v-if="state === 'NO'">Don't want to refer your contact <b> ‘{{ contact.fullName }}’</b> ?</h3>
        <b-card class="contact-info">
          <img :src="`/static/images/avatars/main${contact.avatar || 1}.png`" class="contact-picture"/>
          <div class="contact-name"><b>{{ contact.fullName }}</b></div>
          <div class="jobTitle grey" v-if="contact.latestEmployment">
            {{contact.latestEmployment.title}} at <b>{{contact.latestEmployment.companyName}}</b>
          </div>
          <p>Sometimes you just don’t know if you should forward a connection towards a new role. You might no longer be in touch, or don’t know if they would even be interested, we understand.</p>
          <p>We believe an opportunity provided is always better than an opportunity lost. So if you would be ok with us still getting in contact please let us know, if not just tell us not to.</p>
        </b-card>
      </b-col>
      <div class="footer-buttons">
        <div class="request-buttons">
          <div class="request-button" v-if="state === 'NO'" @click="state = 'NO_REFERRAL_QUESTIONS'">
            <div class="bg-primary-a2 round-icon"><font-awesome-icon icon="times" /> </div> No Thanks
          </div>
          <div class="request-button" v-if="state === 'NOT_SURE'" @click="state = 'NO_REFERRAL_QUESTIONS'">
            <div class="bg-primary-a2 round-icon" @click="state = 'NO_REFERRAL_QUESTIONS'"><font-awesome-icon icon="question" /> </div>      I'm not sure
          </div>
          <div class="request-button primary-background-color" @click="acceptReferralRequest">
            <div class="bg-primary-a2 round-icon" ><font-awesome-icon icon="check" /> </div>
            Refer {{ contact.fullName }}
          </div>
        </div>
      </div>
    </template>

    <template v-else-if="state === 'NO_REFERRAL_QUESTIONS'">
      <b-col cols="12" class="content no-referral">
        <h3>Please provide feedback</h3>
        <b-card class="contact-info">
          <img :src="`/static/images/avatars/main${contact.avatar || 1}.png`" class="contact-picture"/>
          <div class="contact-name"><b>{{ contact.fullName }}</b></div>
          <div class="jobTitle grey" v-if="contact.latestEmployment">
            {{contact.latestEmployment.title}} at <b>{{contact.latestEmployment.companyName}}</b>
          </div>
          <p>Let us know why you aren't able to make the referral</p>
          <b-form-group>
            <b-form-radio-group v-model="declineAnswers.declinedReason" name="radioSubComponent">
              <b-form-radio value="DONT_KNOW_THEM">I don't know them well enough</b-form-radio>
              <b-form-radio value="UNSUITABLE_JOB">They aren't a good fit for the role</b-form-radio>
              <b-form-radio value="UNSUITABLE_COMPANY">They aren't a good fit for the company</b-form-radio>
              <b-form-radio value="OTHER">Other</b-form-radio>
            </b-form-radio-group>
            <b-form-input v-model="declineAnswers.userMessage" v-if="declineAnswers.declinedReason === 'OTHER'"
              type="text" placeholder="Enter your Reason"/>
          </b-form-group>
        </b-card>
        <b-btn variant="primary" @click="declineReferralRequest">Submit Response</b-btn>
      </b-col>
    </template>

    <template v-else-if="state === 'SHARE'">
      <share v-if="referral && job && contact && shareCode" :key="referral.id" :title="`Share this role with ${contact.fullName}`"
        :url="`${$root.origin}/r/${shareCode}`" show-preview :preview-args="{ title: job.title }"
        @modal-close="() => finished()" @track="({ channel }) => trackShareClick({ channel })">
          <template v-slot:text>
            <p>Click one of the buttons below to share with {{contact.fullName}} &amp; we'll let you know when they apply.</p>
          </template>
      </share>
    </template>

  </b-modal>
</template>

<script>
import gql from 'graphql-tag';
import ContactReferralsFragment from '@/graphql/fragments/ContactReferrals.gql';
import LoadingSpinner from '@/components/misc/LoadingSpinner';
import ReferQuestions from '@/components/job-referrals/refer-questions';
import Share from '@/components/Share';

const JobReferralDetailsFragment = gql`
  fragment JobReferralDetails on JobReferral {
    id
    contact {
      id
      fullName
      latestEmployment {
        companyName
        title
      }
    }
    job {
      id
      title
    }
    shareCode
    status
  }
`;

const GetReferralRequest = gql`
  ${JobReferralDetailsFragment}

  query EmployeeGetReferralRequest($referralID: ID!) {
    user {
      id
      referral(where: { id: $referralID }) {
        ...JobReferralDetails
      }
    }
  }
`;
const AcceptReferralRequest = gql`
  ${JobReferralDetailsFragment}

  mutation EmployeeAcceptReferralRequest(
    $referralID: ID!,
  ) {
    updateReferral(
      update: {
        status: ACCEPTED,
      },
      where: {
        id: $referralID,
      },
    ) {
      ...JobReferralDetails
    }
  }
`;
const RejectReferralRequest = gql`
  ${JobReferralDetailsFragment}

  mutation EmployeeRejectReferralRequest(
    $referralID: ID!,
    $declinedReason: String,
    $userMessage: String,
  ) {
    updateReferral(
      update: {
        declinedReason: $declinedReason,
        userMessage: $userMessage,
        status: DECLINED,
      },
      where: {
        id: $referralID,
      },
    ) {
      ...JobReferralDetails
    }
  }
`;

const DEFAULT_DATA = () => ({
  state: null,
  err: null,
  visible: false,
  referral: null,
  referAnswers: {},
  declineAnswers: {},
  sharedBy: null,
});

export default {
  name: 'EmployeeMakesIntroductionModal',
  props: {
    id: {
      type: String,
      default: () => null,
    },
    referralID: {
      type: String,
    },
  },
  components: {
    LoadingSpinner,
    Share,
    ReferQuestions,
    EmptySlot: {
      functional: true,
      render: (h, { data }) => h('template', data, ''),
    },
  },
  data: () => DEFAULT_DATA(),
  apollo: {
    referral: {
      query: GetReferralRequest,
      variables() {
        return {
          referralID: this.referralID,
        };
      },
      update: ({ user: { referral } }) => referral,
      result({ loading, data }) {
        if (!loading && this.state === 'SHARE') {
          this.acceptReferralRequest();
        }
      },
      error(err) {
        this.err = `${err}`;
      },
      skip() {
        return !this.visible;
      },
    },
  },
  computed: {
    contact() {
      const { contact } = this.referral;
      return contact && contact.id ? contact : null;
    },
    job() {
      const { job } = this.referral;
      return job && job.id ? job : null;
    },
    shareCode() {
      const { shareCode } = this.referral;
      return shareCode;
    },
    promptOnClose() {
      if (this.referral && this.referral.id) {
        const { created, updated } = this.referral;
        return created === updated && !this.sharedBy;
      }

      return false;
    },
  },
  methods: {
    async show(state) {
      (data => {
        for (const prop in data) {
          this[prop] = data[prop];
        }
      })({
        ...DEFAULT_DATA(),
        state,
        visible: true,
      });
    },
    async acceptReferralRequest() {
      try {
        await this.$apollo.mutate({
          mutation: AcceptReferralRequest,
          variables: {
            referralID: this.referralID,
          },
        });

        this.state = 'SHARE';
      } catch (err) {
        this.err = `${err}`;
      }
    },
    async declineReferralRequest() {
      const { declinedReason, userMessage } = this.declineAnswers;
      try {
        await this.$apollo.mutate({
          mutation: RejectReferralRequest,
          variables: {
            referralID: this.referralID,
            declinedReason,
            userMessage: declinedReason === 'OTHER' ? userMessage : null,
          },
        });
      } catch (err) {
        this.err = err;
      } finally {
        this.finished();
      }
    },
    async promptRemove() {
      const text = 'You haven’t shared the role yet, are you sure you want to decline?';
      const confirmed = await this.$bvModal.msgBoxConfirm(text, {
        buttonSize: 'sm',
        okTitle: 'Yes',
        okVariant: 'danger',
        cancelTitle: 'No',
        cancelVariant: 'outline-success',
        footerClass: 'p-2',
      });

      if (confirmed) {
        const { referralID } = this;
        const contactID = this.contact ? this.contact.id : null;

        if (referralID) {
          await this.$apollo.mutate({
            mutation: RejectReferralRequest,
            variables: {
              referralID,
              declinedReason: 'CANCELLED',
            },
            update(store) {
              if (contactID) {
                const id = `Contact:${contactID}`;
                const data = store.readFragment({ fragment: ContactReferralsFragment, id });
                if (data && Array.isArray(data.referrals)) {
                  const i = data.referrals.findIndex(r => r && r.id === referralID);
                  if (i >= 0) {
                    data.referrals.splice(i, 1);
                  }
                  store.writeFragment({ fragment: ContactReferralsFragment, id, data });
                }
              }
            },
          });
        }

        this.finished();
      }
    },
    trackShareClick({ channel }) {
      Promise.resolve(this.$root.captureLog({
        eventName: 'REFERRAL_SHARED',
        job: { share: this.shareCode },
        user: { share: this.shareCode },
        referral: { share: this.shareCode },
        data: channel ? [ { key: 'channel', value: channel } ] : null,
      }));
      this.sharedBy = channel || true;
    },
    finished() {
      this.visible = false;
      this.$emit('finished');
    },
  },
};
</script>

<style lang="scss">
@import './../scss/variables';
.employee-respond-to-referral-request {
  .card-body {
    padding: 0;
    padding-top: 1rem;
    .footer-buttons {
      position: relative;
      text-align: center;
      padding: 0;

    }
  }
  .content {
    margin-bottom: 1rem;
    padding: 1rem 2rem 1rem 2rem;

    h3 {
      margin-bottom: 1em;
    }

    .card {
      margin-top: 1em;
      p {
        margin: 1rem;
        // font-size: 0.8rem;
      }

      .card-body {
        padding: 0;
        margin: 0;
        img {
          margin-top: 1rem;
          margin-bottom: 0.4rem;
          height: 60px;
          width: 60px;
        }

        .jobTitle {
          margin-bottom: 1rem;
        }
        text-align: center;
      }
    }

    &.no-referral {
      .form-group{
        label {
          font-weight: normal;
        }
        padding-left: 4rem;
        padding-right: 4rem;
        text-align: left;
        input {
          padding: 0.375rem 0.75rem;
          font-size: 0.8rem;
          margin-top: 0.6rem;
        }
      }
    }

  }

}
</style>



// WEBPACK FOOTER //
// src/modals/employee-respond-to-referral-request.vue